import { useCallback, useSyncExternalStore } from 'react'

const PREFIX = 'veltica:sidebar-favorites:v1:'
const EMPTY: readonly string[] = []
const cache = new Map<string, { raw: string | null; entries: readonly string[] }>()
const memoryOnly = new Set<string>()
const listeners = new Set<() => void>()

function read(scope: string): readonly string[] {
  if (!scope) return EMPTY
  if (memoryOnly.has(scope)) return cache.get(scope)?.entries ?? EMPTY
  let raw: string | null
  try { raw = window.localStorage.getItem(scope) } catch { return cache.get(scope)?.entries ?? EMPTY }
  const saved = cache.get(scope)
  if (saved?.raw === raw) return saved.entries
  let entries: readonly string[] = EMPTY
  try {
    const value: unknown = JSON.parse(raw ?? 'null')
    if (Array.isArray(value)) entries = [...new Set(value.filter((entry): entry is string => typeof entry === 'string' && /^(section|global):[a-zA-Z]+$/.test(entry)))].slice(0, 100)
  } catch { /* Una preferencia dañada no impide abrir el menú. */ }
  cache.set(scope, { raw, entries })
  return entries
}

/** Preferencias de esta cuenta en este navegador; no dependen del proyecto. */
export function useSidebarFavorites(userId: string | undefined) {
  const scope = userId ? PREFIX + userId : ''
  const subscribe = useCallback((listener: () => void) => {
    listeners.add(listener)
    const changed = (event: StorageEvent) => { if (event.key === scope || event.key === null) listener() }
    window.addEventListener('storage', changed)
    return () => { listeners.delete(listener); window.removeEventListener('storage', changed) }
  }, [scope])
  const entries = useSyncExternalStore(subscribe, () => read(scope), () => EMPTY)
  const toggle = (key: string) => {
    if (!scope) return
    const before = read(scope)
    const next = before.includes(key) ? before.filter((entry) => entry !== key) : [...before, key]
    const raw = JSON.stringify(next)
    cache.set(scope, { raw, entries: next })
    try { window.localStorage.setItem(scope, raw) } catch { memoryOnly.add(scope) }
    listeners.forEach((listener) => listener())
  }
  return { favorites: entries, toggleFavorite: toggle }
}
